import Router from 'vue-router'

const TheContainer = () => import('@/containers/TheContainer')
const UserInfo = () => import('@/user/Info')
const UserUpdate = () => import('@/user/UpdateInput')
const DepartmentInfo = () => import('@/department/Info')
const DepartmentUpdate = () => import('@/department/UpdateInput')
const PaidLeaveInfo = () => import('@/request/paid_and_substitute/Info')
const PaidLeaveUpdate = () => import('@/request/paid_and_substitute/UpdateInput')
const OvertimeInfo = () => import('@/request/overtime/Info')
const OvertimeUpdate = () => import('@/request/overtime/UpdateInput')
const HolidayWorkInfo = () => import('@/request/holiday_work/Info')
const HolidayWorkUpdate = () => import('@/request/holiday_work/UpdateInput')
const ProjectInfo = () => import('@/project/ProjectInfo')
const MonthlyReportInfo = () => import('@/admin/monthly_report/Info')
const RosterInfo = () => import('@/admin/roster/Info')
const PaidLeaveInfoAdmin = () => import('@/admin/paid_and_substitute/Info')
const OvertimeInfoAdmin = () => import('@/admin/overtime/Info')
const HolidayWorkInfoAdmin = () => import('@/admin/holiday_work/Info')
const ExpenseInfoAdmin = () => import('@/admin/payoff/Expense')
const TravelCostInfoAdmin = () => import('@/admin/payoff/TravelCostInfo')

const UUID_PATTERN = '[0-9a-fA-F-]{36}'
/*
 * legacyRouteName は旧ルート名（日本語）。これは公開URLの解決に使う。
 * pageTitle は画面上部の見出し。ここを入れないと、利用者には
 * PublicAdminRosterDetail のようなルート名がそのまま見える。
 */
const authMeta = legacyRouteName => ({
  requiresAuth: true,
  publicRouteReference: true,
  legacyRouteName,
  pageTitle: legacyRouteName
})

/**
 * 既存の数値URLを壊さず、その手前へUUID専用ルートを追加する。
 *
 * <p>Vue Router 3のaddRoutesは末尾追加のため、既存の汎用`:id`がUUIDまで先に
 * 捕捉してしまう。初回動的ルート登録の前にmatcherだけを再構築し、UUIDルートを
 * 先頭へ置く。既存routerのguard・history・scroll設定は変更しない。</p>
 */
/**
 * UUID専用ルートの一覧。
 *
 * 登録処理から切り出してある。見出し（meta.pageTitle）が全ルートに
 * 付いているかを、routerを組み立てずに確かめられるようにするため。
 */
export function publicRouteReferenceRouteTable () {
  return [
    {
      path: '/',
      /*
       * **この redirect を外すと入口URLが白紙になる。**
       *
       * この '/' は UUID を先に捕まえるために **本来の '/' より前**へ
       * 置いている。vue-router 3 は同じパスなら先に定義したほうが勝つので、
       * redirect を持たないこちらが本来の '/'（redirect: '/dashboard'）を
       * 隠してしまう。すると `/` は TheContainer だけを描き、
       * 中の router-view に当たる子が無いまま **本文が0文字**になる。
       * 左メニューだけが出るので、壊れていることにも気づきにくい。
       *
       * 隠す以上、隠される側と同じ行き先をここにも書く。
       */
      redirect: '/dashboard',
      component: TheContainer,
      children: [
        {
          path: `project/search/:project_ref(${UUID_PATTERN})`,
          name: 'PublicProjectSearchDetail',
          component: ProjectInfo,
          meta: authMeta('プロジェクト検索詳細')
        },
        {
          path: `users/:user_ref(${UUID_PATTERN})`,
          name: 'PublicUserDetail',
          component: UserInfo,
          meta: authMeta('ユーザ詳細')
        },
        {
          path: `users/:user_ref(${UUID_PATTERN})/edit`,
          name: 'PublicUserUpdate',
          component: UserUpdate,
          meta: authMeta('ユーザ更新')
        },
        {
          path: `department/:department_ref(${UUID_PATTERN})`,
          name: 'PublicDepartmentDetail',
          component: DepartmentInfo,
          meta: authMeta('部署詳細')
        },
        {
          path: `department/:department_ref(${UUID_PATTERN})/edit`,
          name: 'PublicDepartmentUpdate',
          component: DepartmentUpdate,
          meta: authMeta('部署更新')
        },
        {
          path: `paidandsubstitutes/:paid_leave_ref(${UUID_PATTERN})`,
          name: 'PublicPaidLeaveDetail',
          component: PaidLeaveInfo,
          meta: authMeta('休暇申請詳細')
        },
        {
          path: `paidandsubstitutes/:paid_leave_ref(${UUID_PATTERN})/edit`,
          name: 'PublicPaidLeaveEdit',
          component: PaidLeaveUpdate,
          meta: authMeta('休暇申請修正')
        },
        {
          path: `overtimes/:overtime_ref(${UUID_PATTERN})`,
          name: 'PublicOvertimeDetail',
          component: OvertimeInfo,
          meta: authMeta('予定外時間外報告詳細')
        },
        {
          path: `overtimes/:overtime_ref(${UUID_PATTERN})/edit`,
          name: 'PublicOvertimeEdit',
          component: OvertimeUpdate,
          meta: authMeta('予定外時間外報告修正')
        },
        {
          path: `holidayworks/:holiday_work_ref(${UUID_PATTERN})`,
          name: 'PublicHolidayWorkDetail',
          component: HolidayWorkInfo,
          meta: authMeta('休日出勤報告詳細')
        },
        {
          path: `holidayworks/:holiday_work_ref(${UUID_PATTERN})/edit`,
          name: 'PublicHolidayWorkEdit',
          component: HolidayWorkUpdate,
          meta: authMeta('休日出勤報告修正')
        },
        {
          path: `admin/monthlyreports/:report_ref(${UUID_PATTERN})/:user_ref(${UUID_PATTERN})/:year/:month`,
          name: 'PublicAdminMonthlyReportDetail',
          component: MonthlyReportInfo,
          meta: authMeta('業務報告詳細')
        },
        {
          path: `admin/roster/:user_ref(${UUID_PATTERN})/:year/:month`,
          name: 'PublicAdminRosterDetail',
          component: RosterInfo,
          meta: authMeta('勤務表詳細')
        },
        {
          path: `admin/paidandsubstitute/:paid_leave_ref(${UUID_PATTERN})`,
          name: 'PublicAdminPaidLeaveDetail',
          component: PaidLeaveInfoAdmin,
          meta: authMeta('休暇申請詳細(管理者)')
        },
        {
          path: `admin/overtime/:overtime_ref(${UUID_PATTERN})`,
          name: 'PublicAdminOvertimeDetail',
          component: OvertimeInfoAdmin,
          meta: authMeta('予定外時間外報告詳細(管理者)')
        },
        {
          path: `admin/holidaywork/:holiday_work_ref(${UUID_PATTERN})`,
          name: 'PublicAdminHolidayWorkDetail',
          component: HolidayWorkInfoAdmin,
          meta: authMeta('休日出勤報告詳細(管理者)')
        },
        {
          path: `expenseList/:user_ref(${UUID_PATTERN})/:year/:month`,
          name: 'PublicAdminExpenseDetail',
          component: ExpenseInfoAdmin,
          meta: authMeta('経費精算詳細(管理者)')
        },
        {
          path: `travelCostList/:user_ref(${UUID_PATTERN})/:year/:month`,
          name: 'PublicAdminTravelCostDetail',
          component: TravelCostInfoAdmin,
          meta: authMeta('通勤費精算詳細(管理者)')
        }
      ]
    }
  ]
}

/**
 * 既存の数値URLを壊さず、その手前へUUID専用ルートを追加する。
 */
export default function registerPublicRouteReferenceRoutes (router) {
  if (!router || router.__preikaPublicRouteReferencesRegistered) return
  router.__preikaPublicRouteReferencesRegistered = true

  const replacement = new Router({
    routes: [...publicRouteReferenceRouteTable(), ...(router.options.routes || [])]
  })
  router.matcher = replacement.matcher
}
